<template>
  <main class="u-container text-center pb-10">
    <h2>Welcome Back</h2>
    <p class="mt-2">
      One moment while we process your Change-of-Address order.
    </p>
    <AppLoader class="mt-6" />
  </main>
</template>

<script>
import { validate } from 'uuid';
import Cookies from 'js-cookie';
import { isDesktop } from '../utils/deviceType';
import AppLoader from '../components/AppLoader.vue';

export default {
  name: 'Action',
  components: {
    AppLoader,
  },
  data() {
    return {
      coa: {},
      dcoa: false,
      mover: {},
      oldAddress: {},
      newAddress: {},
      forwardType: '',
      moverType: '',
      businessName: '',
    };
  },
  computed: {
    flagAmpersendEnabled() {
      return this.$store.get('flagAmpersendEnabled');
    },
    coaUUIDUrl() {
      return this.$router?.params?.orderUuid;
    },
    isMilitaryCOA() {
      return this.addressIsMilitary(this.oldAddress) || this.addressIsMilitary(this.newAddress);
    },
  },
  async mounted() {
    // Route params need to be destructured and set on $coa prior to API calls
    const uuid = new URL(window.location)?.pathname?.split('/')?.pop();
    const {
      cuid,
      ord: orderNumber,
      coa_uuid: coaId,
      utm_campaign: campaign,
      utm_medium: medium,
      utm_source: source,
    } = this.$route.query;

    const parsedUUID = coaId.split(/\?/).filter((item) => validate(item))?.pop();
    const coaUUID = validate(uuid) ? uuid : parsedUUID;

    this.$store.set('instantSavings/orderUuid', coaUUID);
    // Clear any deals opt-in left over from a previous order in this session so
    // an order that never reaches the instant-savings step cannot send deals
    this.$store.set('instantSavings/offersOptIn', '');
    if (!coaUUID) {
      this.$router.replace({ name: 'processing-error' });
      return;
    }
    this.$coa.coaUUID = coaUUID;
    if (this.coaUUIDUrl) {
      sessionStorage.setItem('coaUUID', this.coaUUIDUrl);
    }
    await this.hydrateCOA();
    await this.hydrateAddresses();
    await this.hydrateMover();
    this.$store.set('instantSavings/email', this.mover.email || '');
    this.$store.set('instantSavings/firstName', this.mover.firstName || '');
    this.$store.set('instantSavings/lastName', this.mover.lastName || '');
    this.$store.set('instantSavings/mobilePhone',  this.mover.mobilePhone || '');
    this.$store.set('instantSavings/oldAddress', this.oldAddress || {});
    this.$store.set('instantSavings/newAddress', this.newAddress || {});
    this.$store.set('instantSavings/businessName', this.coa.businessName || '');

    const destination = {
      name: 'confirm-order',
      params: { orderUuid: coaUUID },
      query: {
        dcoa: this.dcoa,
        utm_campaign: campaign || 'default',
        utm_medium: medium || 'sp',
        utm_source: source || 'usps',
      },
    };

    const hasMultipleBusiness = this.coa.multipleAddresses;
    const { ivsVerificationPassed } = this.coa || null;
    const cfpsId = await this.$coa.coaCommit({
      coaUUID, cuid, orderNumber, hasMultipleBusiness, ivsVerificationPassed,
    });
    const isCommitted = typeof cfpsId === 'string' || typeof cfpsId === 'number' || cfpsId === null;
    Cookies.set('coa_complete', cfpsId, { sameSite: 'strict' });

    if (!isCommitted) {
      Object.assign(destination, {
        name: 'processing-error',
        query: {},
      });
    } else {
      this.registerAmpersendSuccessPageContext();
    }

    Object.assign(destination.query, { cfpsId });

    if (isCommitted && this.flagAmpersendEnabled && !this.isMilitaryCOA) {
      this.$store.set('instantSavings/token', this.$coa.token);
      this.$store.set('instantSavings/orderUuid', this.$coa.coaUUID);
      await this.$nextTick();

      let anonymousId = '';
      try {
        anonymousId = await this.$store.dispatch(
          'instantSavings/identify',
          {
            informedDelivery: this.coa.informedDelivery,
            deviceType: isDesktop() ? 'DESKTOP' : 'MOBILE',
          },
        );
      } catch {
        this.$store.set('instantSavings/anonymousId', '');
      }

      if (typeof anonymousId === 'string' && anonymousId) {
        this.$amp.setAnonymousID(anonymousId);
        this.$amp.initSDK();
      } else {
        this.$store.set('instantSavings/anonymousId', '');
      }

      if (this.$store.get('config').liveOffersEnabled) {
        try {
          await this.$store.dispatch('instantSavings/getOffers', {
            schemas: [
              'MGO Advantage Single',
              'MGO Advantage Double',
            ],
            viewAt: this.$route.query.viewAt ? new Date(this.$route.query.viewAt).toISOString() : '',
          });
        } catch {
          this.$store.set('instantSavings/offers', []);
        }
      }
    }

    if (isCommitted && this.$store.get('instantSavings/offers').length) {
      Object.assign(destination, {
        name: 'instant-savings',
      });
    }

    if (this.isMilitaryCOA) {
      Object.assign(destination.query, {
        mcoa: true,
      });
    }

    this.$router.replace(destination);
  },
  methods: {
    registerAmpersendSuccessPageContext() {
      const context = {
        email: this.mover.email || '',
        firstName: this.mover.firstName || '',
        lastName: this.mover.lastName || '',
        businessName: this.coa.businessName || '',
        mobilePhone: this.mover.mobilePhone || '',
        oldAddress: { ...this.oldAddress },
        newAddress: { ...this.newAddress },
        userMoveDate: this.coa.startDate || '',
        moverType: this.moverType || '',
      };

      window.mgoAmpersend = {
        ...window.mgoAmpersend,
        getSuccessPageContext: () => ({
          ...context,
          oldAddress: { ...context.oldAddress },
          newAddress: { ...context.newAddress },
        }),
      };
    },
    async hydrateCOA() {
      const { uuid, ...coa } = await this.$coa.coaGet() || [];

      if (uuid) {
        this.coa = coa;
        this.dcoa = coa.dcoa;
        this.forwardType = coa.forwardType;
        this.moverType = coa.moverType;
      }
    },
    async hydrateMover() {
      try {
        const { MOVER } = await this.$coa.personGetAll();
        if (MOVER) {
          this.mover = MOVER;
        }
      } catch {
        this.mover = {};
      }
    },
    async hydrateAddresses() {
      const { addresses } = await this.$coa.addressGetAll();
      const [address1, address2, address3] = addresses;
      [
        address1,
        address2,
        address3,
      ]
        .filter((a) => a)
        .forEach(({ addressType, ...address }) => {
          if (addressType === 'BILLING') {
            return;
          }

          this[`${addressType.toLowerCase()}Address`] = { addressType, ...address };
        });
    },
    addressIsMilitary(address = {}) {
      return address.military === true || address.militaryZipCode === true;
    },
  },
};
</script>
